import { CampaignInquiryProductInput, Client } from "src/graphql/generated/graphql";
import { DeliveryState } from "src/hooks/campaigns";
import { DeepPartial } from "src/types/utils";

import type { CreateCampaignInquiryFormValues } from "./formContext";

export const createCampaignInquiryFormKey = "createCampaignInquiryForm";

const mapProduct = (product: DeepPartial<CampaignInquiryProductInput>) => ({
    ...product,
    category: {
        key: product.category?.key || "",
    },
    attributes: product.attributes
        ? product.attributes.map((attr) => ({ id: attr.id || "", locale: attr.locale || "", name: attr.name || "" }))
        : [],
    destinations: product.destinations
        ? product.destinations.map((dest) => ({
              key: dest.key || "",
              name: dest.name || "",
              type: dest.type || "text",
              config: dest.config || "",
              configFields: dest.configFields
                  ? dest.configFields.map((field) => ({
                        label: field.label || "",
                        path: field.path || "",
                        type: field.type || "text",
                        placeholder: field.placeholder || "",
                        isRequiredForApproval: field.isRequiredForApproval || false,
                        urlStripQueryString: field.urlStripQueryString || false,
                    }))
                  : [],
          }))
        : [],
});

export type PartialCreateCampaignInquiryInput = DeepPartial<CreateCampaignInquiryFormValues>;

export const getFormValues = (values?: PartialCreateCampaignInquiryInput): PartialCreateCampaignInquiryInput => ({
    name: values?.name ?? "",
    clientId: values?.clientId ?? "",
    tenantId: values?.tenantId ?? null,
    displayTenantIds: values?.displayTenantIds ?? [],
    mainTenantId: values?.mainTenantId ?? null,
    products: values?.products ? values.products.map(mapProduct) : [],
    description: values?.description ?? null,
    settings: {
        capacity: values?.settings?.capacity ?? 0,
        openAt: values?.settings?.openAt ?? new Date().getTime(),
    },
    restrictions: values?.restrictions ?? null,
    pricing: {
        isFree: values?.pricing?.isFree ?? true,
        discountPercent: values?.pricing?.discountPercent ?? 100,
        discountCodes: values?.pricing?.discountCodes ?? [],
    },
    delivery: {
        freeOfCharge: values?.delivery?.freeOfCharge ?? false,
        type: values?.delivery?.type ?? DeliveryState.PersonalPickup,
        notes: values?.delivery?.notes ?? "",
    },
    questionnaire: values?.questionnaire ?? null,
    thumbnailImage: values?.thumbnailImage ?? null,
    images: values?.images ?? [],
    additionalNotes: values?.additionalNotes ?? "",
});

export const getDefaultValues = (client: Client, isSkipLocalStorage?: boolean): PartialCreateCampaignInquiryInput => {
    const localValues = localStorage.getItem(createCampaignInquiryFormKey);

    if (localValues && client && !isSkipLocalStorage) {
        const values = JSON.parse(localValues);
        if (values.clientId === client.id) {
            return values;
        }

        localStorage.removeItem(createCampaignInquiryFormKey);
    }

    return {
        ...getFormValues(),
        clientId: client?.id,
    };
};
